// Regras da Área de Treinamento (conteúdo, visibilidade, progresso e texto formatado).
// Fica fora dos componentes para ser testado (src/test/training.test.ts).

export type TrainingKind = 'tutorial' | 'atendimento';

export const KIND_LABELS: Record<TrainingKind, string> = { tutorial: 'Tutoriais', atendimento: 'Atendimento' };

export interface TrainingItem {
    id: number | string;
    company_id?: number;
    kind: TrainingKind;
    title: string;
    summary?: string | null;
    body: string;
    video_url?: string | null;
    category: string;
    /** Papéis que enxergam o conteúdo; vazio = todos. */
    audience_roles: string[];
    is_required: boolean;
    published: boolean;
    position: number;
    created_at?: string;
    updated_at?: string;
    /** Conteúdo que já vem no sistema (não editável; progresso fica no navegador). */
    builtin?: boolean;
    /** Curso a que a aula pertence; nulo nas aulas antigas (entram em "Tutoriais" ou "Atendimento" pelo kind). */
    course_id?: number | null;
    /** Capa da aula (link). Sem ela, usa a miniatura do YouTube ou um degradê. */
    thumbnail_url?: string | null;
    duration_seconds?: number | null;
    /** Vídeo enviado pela gestão: caminho no bucket privado training-media. */
    video_path?: string | null;
    /** PDFs anexados à aula (arquivos no bucket privado training-media). */
    documents?: TrainingDocument[];
}

/** PDF anexado a uma aula. `path` é o arquivo no bucket; `name` é o nome mostrado e usado ao baixar. */
export interface TrainingDocument {
    path: string;
    name: string;
    size: number;
}

export const MAX_LESSON_DOCUMENTS = 10;

/** Lê o campo `documents` do banco sem confiar no formato: ignora entradas inválidas e limita a quantidade. */
export function normalizeDocuments(value: unknown): TrainingDocument[] {
    if (!Array.isArray(value)) return [];
    const docs: TrainingDocument[] = [];
    for (const raw of value) {
        if (!raw || typeof raw !== 'object') continue;
        const { path, name, size } = raw as Record<string, unknown>;
        if (typeof path !== 'string' || !path.trim() || path.includes('..')) continue;
        const fallback = path.split('/').pop() || 'documento.pdf';
        docs.push({
            path,
            name: typeof name === 'string' && name.trim() ? name.trim().slice(0, 150) : fallback,
            size: typeof size === 'number' && Number.isFinite(size) && size > 0 ? size : 0,
        });
        if (docs.length >= MAX_LESSON_DOCUMENTS) break;
    }
    return docs;
}

/** Nome do arquivo ao baixar: garante a extensão .pdf e tira caracteres que o sistema operacional não aceita. */
export function documentDownloadName(name: string): string {
    const clean = Array.from(name).filter(ch => ch.charCodeAt(0) >= 32 && !'\\/:*?"<>|'.includes(ch)).join('').trim() || 'documento';
    return /\.pdf$/i.test(clean) ? clean : `${clean}.pdf`;
}

export const TRAINING_ROLES: { value: string; label: string }[] = [
    { value: 'vendedor', label: 'Vendedor' },
    { value: 'sdr', label: 'SDR' },
    { value: 'navegador', label: 'Navegador' },
    { value: 'operacao', label: 'Operação' },
    { value: 'cs', label: 'Customer Success' },
    { value: 'gestor', label: 'Gestor' },
];

const MANAGER_ROLES = ['gestor', 'ceo', 'admin', 'superadmin'];

export const isManagerRole = (role: string | null | undefined) => MANAGER_ROLES.includes((role || '').toLowerCase().trim());

/** Gestão vê tudo (inclusive rascunhos); os demais, só o publicado e destinado ao seu papel. */
export function isVisibleToRole(item: Pick<TrainingItem, 'published' | 'audience_roles'>, role: string | null | undefined): boolean {
    if (isManagerRole(role)) return true;
    if (!item.published) return false;
    return item.audience_roles.length === 0 || item.audience_roles.includes((role || '').toLowerCase().trim());
}

export interface TrainingProgress {
    done: number;
    total: number;
    percent: number;
    requiredDone: number;
    requiredTotal: number;
}

export function computeProgress(items: Pick<TrainingItem, 'id' | 'is_required'>[], completed: Set<number | string>): TrainingProgress {
    const total = items.length;
    const done = items.filter(i => completed.has(i.id)).length;
    const required = items.filter(i => i.is_required);
    return {
        done,
        total,
        percent: total === 0 ? 0 : Math.round((done / total) * 100),
        requiredDone: required.filter(i => completed.has(i.id)).length,
        requiredTotal: required.length,
    };
}

const norm = (v: string) => v.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

export function filterItems<T extends Pick<TrainingItem, 'title' | 'summary' | 'body' | 'category'>>(items: T[], query: string, category: string): T[] {
    const q = norm(query);
    return items.filter(i =>
        (!category || i.category === category) &&
        (!q || norm(i.title).includes(q) || norm(i.summary || '').includes(q) || norm(i.body).includes(q)));
}

export function groupByCategory<T extends Pick<TrainingItem, 'category' | 'position' | 'title'>>(items: T[]): { category: string; items: T[] }[] {
    const map = new Map<string, T[]>();
    items.forEach(i => map.set(i.category, [...(map.get(i.category) || []), i]));
    return Array.from(map.entries())
        .sort(([a], [b]) => (a === 'Geral' ? -1 : b === 'Geral' ? 1 : a.localeCompare(b, 'pt-BR')))
        .map(([category, list]) => ({
            category,
            items: [...list].sort((a, b) => a.position - b.position || a.title.localeCompare(b.title, 'pt-BR')),
        }));
}

export interface VideoEmbed {
    provider: 'youtube' | 'vimeo' | 'loom' | 'drive';
    embedUrl: string;
}

/** Transforma o link colado (YouTube, Vimeo ou Loom) no endereço de incorporação. Qualquer outro link devolve null. */
export function parseVideoEmbed(url: string | null | undefined): VideoEmbed | null {
    const raw = (url || '').trim();
    if (!raw) return null;
    let u: URL;
    try {
        u = new URL(/^https?:\/\//i.test(raw) ? raw : `https://${raw}`);
    } catch {
        return null;
    }
    const host = u.hostname.replace(/^www\./, '');
    const idOk = (id: string | null | undefined): id is string => !!id && /^[\w-]{6,}$/.test(id);

    if (host === 'youtu.be') {
        const id = u.pathname.split('/')[1];
        return idOk(id) ? { provider: 'youtube', embedUrl: `https://www.youtube-nocookie.com/embed/${id}` } : null;
    }
    if (host === 'youtube.com' || host === 'm.youtube.com') {
        const parts = u.pathname.split('/').filter(Boolean);
        const id = u.searchParams.get('v') || (['shorts', 'embed', 'live'].includes(parts[0]) ? parts[1] : null);
        return idOk(id) ? { provider: 'youtube', embedUrl: `https://www.youtube-nocookie.com/embed/${id}` } : null;
    }
    if (host === 'vimeo.com' || host === 'player.vimeo.com') {
        const id = u.pathname.split('/').filter(Boolean).find(p => /^\d+$/.test(p));
        return id ? { provider: 'vimeo', embedUrl: `https://player.vimeo.com/video/${id}` } : null;
    }
    if (host === 'loom.com') {
        const parts = u.pathname.split('/').filter(Boolean);
        const id = (parts[0] === 'share' || parts[0] === 'embed') ? parts[1] : null;
        return idOk(id) ? { provider: 'loom', embedUrl: `https://www.loom.com/embed/${id}` } : null;
    }
    if (host === 'drive.google.com') {
        const parts = u.pathname.split('/').filter(Boolean);
        const id = parts[0] === 'file' && parts[1] === 'd' ? parts[2] : u.searchParams.get('id');
        return idOk(id) ? { provider: 'drive', embedUrl: `https://drive.google.com/file/d/${id}/preview` } : null;
    }
    return null;
}

const DIRECT_VIDEO_RE = /\.(mp4|webm|mov|m4v|ogv)$/i;

/** Link direto para um arquivo de vídeo (https e extensão de vídeo): toca no player do navegador, sem incorporar site de terceiros. */
export function directVideoUrl(url: string | null | undefined): string | null {
    const raw = (url || '').trim();
    if (!raw) return null;
    try {
        const u = new URL(raw);
        return u.protocol === 'https:' && DIRECT_VIDEO_RE.test(u.pathname) ? u.toString() : null;
    } catch {
        return null;
    }
}

/** Qualquer link de vídeo aceito: YouTube, Vimeo, Loom, Google Drive ou arquivo direto (.mp4, .webm...). */
export const isSupportedVideoUrl = (url: string | null | undefined) => !!parseVideoEmbed(url) || !!directVideoUrl(url);

// ---------- Texto formatado (markdown simples, renderizado sem HTML cru) ----------

export type BodyBlock =
    | { type: 'h2' | 'h3' | 'p' | 'quote'; text: string }
    | { type: 'ul' | 'ol'; items: string[] };

export interface InlineSegment { text: string; bold: boolean }

/** Negrito com **texto**; o resto é texto puro (nada de HTML). */
export function parseInline(text: string): InlineSegment[] {
    const out: InlineSegment[] = [];
    const re = /\*\*([^*]+)\*\*/g;
    let last = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(text)) !== null) {
        if (m.index > last) out.push({ text: text.slice(last, m.index), bold: false });
        out.push({ text: m[1], bold: true });
        last = m.index + m[0].length;
    }
    if (last < text.length) out.push({ text: text.slice(last), bold: false });
    return out;
}

/** Títulos (#, ##), listas (-, 1.), citação (>) e parágrafos separados por linha em branco. */
export function parseBody(body: string): BodyBlock[] {
    const blocks: BodyBlock[] = [];
    let para: string[] = [];
    let list: { type: 'ul' | 'ol'; items: string[] } | null = null;

    const flushPara = () => { if (para.length) { blocks.push({ type: 'p', text: para.join('\n') }); para = []; } };
    const flushList = () => { if (list) { blocks.push(list); list = null; } };

    for (const rawLine of (body || '').replace(/\r\n/g, '\n').split('\n')) {
        const line = rawLine.trimEnd();
        const trimmed = line.trim();
        if (!trimmed) { flushPara(); flushList(); continue; }

        const heading = /^(#{1,3})\s+(.+)$/.exec(trimmed);
        if (heading) {
            flushPara(); flushList();
            blocks.push({ type: heading[1].length === 1 ? 'h2' : 'h3', text: heading[2] });
            continue;
        }
        const bullet = /^[-*•]\s+(.+)$/.exec(trimmed);
        const numbered = /^\d+[.)]\s+(.+)$/.exec(trimmed);
        if (bullet || numbered) {
            flushPara();
            const type = bullet ? 'ul' : 'ol';
            if (list && list.type !== type) flushList();
            list = list || { type, items: [] };
            list.items.push((bullet || numbered)![1]);
            continue;
        }
        const quote = /^>\s?(.*)$/.exec(trimmed);
        if (quote) {
            flushPara(); flushList();
            blocks.push({ type: 'quote', text: quote[1] });
            continue;
        }
        flushList();
        para.push(trimmed);
    }
    flushPara(); flushList();
    return blocks;
}

export function readingMinutes(body: string): number {
    const words = (body || '').trim().split(/\s+/).filter(Boolean).length;
    return Math.max(1, Math.ceil(words / 200));
}

export interface TrainingDraft {
    title: string;
    body: string;
    video_url?: string | null;
    /** Vídeo enviado para o sistema; conta como vídeo mesmo sem link. */
    video_path?: string | null;
    /** PDFs anexados; contam como conteúdo mesmo sem texto nem vídeo. */
    documents?: TrainingDocument[];
    category: string;
}

/** Mensagem de erro do formulário, ou null quando pode salvar. */
export function validateTrainingDraft(d: TrainingDraft): string | null {
    if (!d.title.trim()) return 'Dê um título ao conteúdo.';
    if (d.title.trim().length > 120) return 'O título pode ter no máximo 120 caracteres.';
    if (!d.category.trim()) return 'Escolha uma categoria.';
    const hasLink = !!(d.video_url || '').trim();
    const hasVideo = hasLink || !!d.video_path;
    if (!d.body.trim() && !hasVideo && !(d.documents && d.documents.length > 0)) return 'Escreva o conteúdo, adicione um vídeo ou anexe um PDF.';
    if (hasLink && !isSupportedVideoUrl(d.video_url)) return 'O vídeo precisa ser um link do YouTube, Vimeo, Loom, Google Drive ou um arquivo .mp4/.webm.';
    if (d.body.length > 20000) return 'O texto passou do limite de 20.000 caracteres.';
    return null;
}

export interface MemberProgressRow {
    userId: string;
    name: string;
    done: number;
    total: number;
    requiredDone: number;
    requiredTotal: number;
}

/** Quanto cada pessoa do time já concluiu (só do conteúdo que ela enxerga). */
export function memberProgress(
    members: { id: string; name: string; role: string }[],
    items: TrainingItem[],
    completions: { user_id: string; item_id: number | string }[],
): MemberProgressRow[] {
    const byUser = new Map<string, Set<number | string>>();
    completions.forEach(c => byUser.set(c.user_id, (byUser.get(c.user_id) || new Set()).add(c.item_id)));
    return members.map(m => {
        const visible = items.filter(i => i.published && !i.builtin && (i.audience_roles.length === 0 || i.audience_roles.includes(m.role.toLowerCase())));
        const p = computeProgress(visible, byUser.get(m.id) || new Set());
        return { userId: m.id, name: m.name, done: p.done, total: p.total, requiredDone: p.requiredDone, requiredTotal: p.requiredTotal };
    }).sort((a, b) => pendingRequired(b) - pendingRequired(a) || a.name.localeCompare(b.name, 'pt-BR'));
}

const pendingRequired = (r: MemberProgressRow) => r.requiredTotal - r.requiredDone;
